import { c as _c } from "react/compiler-runtime";
import { FetcherProvider } from "@website/fetcher/provider";
import { useAuthPages } from "@website/hooks/auth-manager";
import { ApplicationNameType, mapProductForUserTracking, startUserTracking } from "@website/tracking/user";
import { SnackbarProvider } from "@website/ui/snackbar";
import { getChannel } from "@website/utils/channel/get-channel";
import { isChannel } from "@website/utils/channel/helpers";
import { Channels } from "@website/utils/channel/types";
import { InjectExperimentConfigScript, configureExperimentServerSide, type GBFeatures } from "@website/utils/experiments/server";
import { SERVER_NOW } from "@website/utils/server-now";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import { SchemaLocalBusiness } from "@website/widget/scripts/schema-local-business";
import { SchemaOrganization } from "@website/widget/scripts/schema-organization";
import { Scripts } from "@website/widget/scripts/scripts";
import { WebVitals } from "@website/widget/scripts/web-vitals";
import { WelcomeScreenClientHandler } from "@website/widget/welcom-screen-handler";
import toString from "lodash-es/toString";
import NextApp, { AppContext, AppProps } from "next/app";
import Head from "next/head";
import { Suspense } from "react";
startUserTracking({
  product: mapProductForUserTracking(process.env.NEXT_PUBLIC_APP_NAME as ApplicationNameType)
});
const webEngageLicenseCode = {
  android: "11b5650c0",
  ios: "11b5650c0",
  jek: "~47b6574d",
  ap: "11b5650c0",
  app: "11b5650c0",
  ads: "11b5650c0",
  "snapp-ir": "~47b6574d",
  snapp: "~47b6574d",
  website: "11b5650c0",
  telesale: "11b5650c0"
}[getChannel() as string];
const gtmLicenseCode = {
  android: "GTM-MT54VMN",
  ios: "GTM-KJPQFH4",
  jek: "GTM-P8WGVRK",
  ap: "GTM-KJPQFH4",
  app: "GTM-KJPQFH4",
  ads: "GTM-KJPQFH4",
  "snapp-ir": "GTM-5HTF8BD",
  snapp: "GTM-P8WGVRK",
  website: "GTM-MT54VMN",
  telesale: "GTM-MT54VMN"
}[getChannel() as string];
const indexedChannels: Record<string, Array<Channels>> = {
  checkout: [],
  villa: ["website", "jek"],
  tour: ["website", "jek", "snapp-ir"],
  visa: ["website", "jek", "snapp-ir"],
  accommodation: [],
  "hotel-international": [],
  "hotel-domestic": ["website", "jek", "snapp-ir"]
};
export const productsToNotFollow = ["hotel-international"];
const shouldNotFollowPage = productsToNotFollow.includes(process?.env?.NEXT_PUBLIC_APP_NAME as string);
type WidgetPageProperties = {
  gbFeatures?: GBFeatures;
};
export const App = t0 => {
  const $ = _c(62);
  const {
    Component,
    pageProps
  } = t0;
  useAuthPages();
  const {
    gbFeatures
  } = pageProps as WidgetPageProperties;
  let t1;
  if ($[0] !== pageProps.seoData) {
    t1 = pageProps?.seoData ?? {};
    $[0] = pageProps.seoData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    title,
    description,
    fallbackTitle,
    resolvedUrl
  } = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = replaceSnappTripBasedOnChannel("\u0627\u0633\u0646\u067E \u062A\u0631\u06CC\u067E");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const basicTitle = t2;
  const finalTitle = title || `${fallbackTitle ? `${fallbackTitle} | ` : ""}${basicTitle}`;
  const finalDescription = description || finalTitle;
  let t3;
  if ($[3] !== resolvedUrl) {
    t3 = {
      name: "twitter:url",
      content: resolvedUrl
    };
    $[3] = resolvedUrl;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== finalTitle) {
    t4 = {
      name: "twitter:title",
      content: finalTitle
    };
    $[5] = finalTitle;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== finalDescription) {
    t5 = {
      name: "twitter:description",
      content: finalDescription
    };
    $[7] = finalDescription;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t10;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      property: "og:image",
      content: "/favicon.ico"
    };
    t7 = {
      property: "og:image:alt",
      content: `لوگوی ${basicTitle}`
    };
    t8 = {
      property: "og:locale",
      content: "fa_IR"
    };
    t9 = {
      property: "og:site_name",
      content: basicTitle
    };
    t10 = {
      property: "og:type",
      content: "website"
    };
    $[9] = t10;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t10 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t11;
  if ($[14] !== resolvedUrl) {
    t11 = {
      property: "og:url",
      content: resolvedUrl
    };
    $[14] = resolvedUrl;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== finalDescription) {
    t12 = {
      property: "og:description",
      content: finalDescription
    };
    $[16] = finalDescription;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== finalTitle) {
    t13 = {
      property: "og:title",
      content: finalTitle
    };
    $[18] = finalTitle;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t11 || $[21] !== t12 || $[22] !== t13 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5) {
    t14 = [t3, t4, t5, t6, t7, t8, t9, t10, t11, t12, t13];
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const metaProperties = t14;
  let t15;
  if ($[27] !== finalTitle) {
    t15 = <title>{finalTitle}</title>;
    $[27] = finalTitle;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== finalDescription) {
    t16 = <meta content={finalDescription} name="description" />;
    $[29] = finalDescription;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== resolvedUrl) {
    t17 = resolvedUrl && <link href={resolvedUrl} rel="canonical" />;
    $[31] = resolvedUrl;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== metaProperties) {
    t18 = metaProperties.map(_temp);
    $[33] = metaProperties;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== pageProps.seoData?.robots) {
    t19 = shouldNotFollowPage ? <meta content="noindex,nofollow" name="robots" /> : pageProps?.seoData?.robots ? <meta content={pageProps?.seoData?.robots} name="robots" /> : !isChannel(indexedChannels[toString(process?.env?.NEXT_PUBLIC_APP_NAME)]) && <meta content="noindex,follow" name="robots" />;
    $[35] = pageProps.seoData?.robots;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <meta name="google" content="notranslate" />;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== gbFeatures) {
    t21 = gbFeatures && <InjectExperimentConfigScript features={gbFeatures} />;
    $[38] = gbFeatures;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t15 || $[41] !== t16 || $[42] !== t17 || $[43] !== t18 || $[44] !== t19 || $[45] !== t21) {
    t22 = <Head>{t15}{t16}{t17}{t18}{t19}{t20}{t21}</Head>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Suspense fallback={null}><WelcomeScreenClientHandler /></Suspense>;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== pageProps) {
    t24 = pageProps ?? {};
    $[48] = pageProps;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== Component || $[51] !== t24) {
    t25 = <FetcherProvider>{t23}<SnackbarProvider duration="long"><Component {...t24} /></SnackbarProvider></FetcherProvider>;
    $[50] = Component;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  let t27;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <SchemaOrganization />;
    t27 = <SchemaLocalBusiness />;
    $[53] = t26;
    $[54] = t27;
  } else {
    t26 = $[53];
    t27 = $[54];
  }
  let t28;
  if ($[55] !== pageProps.serverNow) {
    t28 = <Scripts gtmLicence={gtmLicenseCode} serverNow={pageProps.serverNow} webengageLicence={webEngageLicenseCode} />;
    $[55] = pageProps.serverNow;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <WebVitals />;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== t22 || $[59] !== t25 || $[60] !== t28) {
    t30 = <>{t22}{t25}{t26}{t27}{t28}{t29}</>;
    $[58] = t22;
    $[59] = t25;
    $[60] = t28;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  return t30;
};
const getAppInitialProperties = async (appContext: AppContext) => {
  const appProperties = await NextApp.getInitialProps(appContext);
  const gbFeatures = appContext.ctx.req ? await configureExperimentServerSide() : undefined;
  return {
    ...appProperties,
    pageProps: {
      ...appProperties.pageProps,
      serverNow: SERVER_NOW(),
      ...(gbFeatures ? {
        gbFeatures
      } : {})
    }
  };
};
App.getInitialProps = getAppInitialProperties;
function _temp(properties) {
  return <meta key={properties.name || properties.property} {...properties} />;
}